import { useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { AxiosError, AxiosResponse } from 'axios';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { ChatActionType, type BrandInsightsInstanceContext, type ChatAction } from '@trinity/chat';
import { ActiveChatSessionContext, ChatSessionFiltersContext, SetPendingScrollContext } from '../context';

type MutationVars = {
  identifier: string;
  parentQueryIdentifier: string;
  query: string;
  action: ChatAction | null;
  context?: BrandInsightsInstanceContext.ItemWithMetadata;
  promptIdentifier?: string;
};

export const useSubmitQueryMutation = () => {
  const chat = useContext(ActiveChatSessionContext);
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const setPendingScroll = useContext(SetPendingScrollContext);
  const brandIdentifier = useActiveBrandIdentifier();

  const apiClient = useChatApiClient();

  const mutation = useMutation({
    mutationKey: ['chat:message:submit'],
    mutationFn: (vars: MutationVars) => {
      return apiClient.chat.submitChatQuery({
        action: vars.action,
        chatIdentifier: chat.identifier,
        identifier: vars.identifier,
        parentQueryIdentifier: vars.parentQueryIdentifier,
        query: vars.query,
        context: vars.context || chat.context,
        filters,
        brandIdentifier,
        promptIdentifier: vars.promptIdentifier,
      });
    },
    onMutate: ({ action, identifier, parentQueryIdentifier, query }) => {
      dispatch({
        type: 'query-submitted',
        payload: {
          attachments: [],
          chatIdentifier: chat.identifier,
          identifier,
          parentQueryIdentifier,
          value: query,
          hideUserMessage: !!action && action.type !== ChatActionType.ChartQuantAnalyses,
          brandIdentifier,
        },
      });
      if (setPendingScroll) {
        setPendingScroll();
      }
    },
    onError: (error: AxiosError<AxiosResponse>, vars) => {
      if (error.response?.status === 504 || error.status === 504) {
        return;
      }

      if (error.response?.status === 429 || error.status === 429) {
        return;
      }

      const traceId = error.response?.headers['x-sentry-trace-id'] as string;
      dispatch({
        type: 'query-errored',
        payload: {
          langfuseTraceUrl: null,
          queryIdentifier: vars.identifier,
          traceId,
        },
      });
      if (setPendingScroll) {
        setPendingScroll();
      }
    },
  });

  return mutation;
};
